"use client";

import React from "react";
import { createPortal } from "react-dom";
import {
  autoUpdate,
  flip,
  limitShift,
  offset,
  shift,
  size,
  useFloating,
  type Placement,
  type VirtualElement,
} from "@floating-ui/react-dom";

/**
 * AnchoredPopover — the one shared positioning layer for every hand-rolled
 * dropdown/menu/picker in the app.
 *
 * What it guarantees:
 * - Portals to <body>, so no `overflow: hidden` / transformed ancestor can
 *   clip or mis-anchor it (the center-peek card is `overflow-hidden`; a
 *   transformed ancestor breaks `position: fixed` — both bugs disappear here).
 * - Measures and flips/shifts via Floating UI so it never renders outside the
 *   window, and re-positions on scroll/resize (`autoUpdate`).
 * - Constrains to the nearest `[data-popover-boundary]` ancestor of the anchor
 *   (e.g. the center-peek card) instead of the viewport when one exists —
 *   Floating UI clips to the intersection of that element and the viewport.
 * - When `onClose` is given it owns dismissal: pointerdown outside (which also
 *   means opening any other popover closes this one first) and Escape.
 *   Escape stops propagation so an outer surface (center peek, modals) doesn't
 *   close on the same keypress.
 * - Nested popovers (a submenu portaled from inside a parent panel) register
 *   themselves with their ancestors, so a click inside a child never counts as
 *   "outside" the parent.
 *
 * The caller keeps owning the `open` state — render this conditionally, pass
 * the trigger element (or a viewport point for context menus) as `anchor`.
 */

type AnchorPoint = { x: number; y: number };
export type AnchoredPopoverAnchor =
  | HTMLElement
  | AnchorPoint
  | React.RefObject<HTMLElement | null>
  | null;

export const POPOVER_BOUNDARY_ATTR = "data-popover-boundary";
/** Marks a floating panel as part of the popover layer. Legacy self-positioned
 *  panels that open ABOVE a popover (e.g. the emoji picker) can set it too, so
 *  parent dismissal treats their clicks as inside and the scroll lock lets
 *  them scroll. */
export const POPOVER_PANEL_ATTR = "data-anchored-popover";

/** For legacy outside-click handlers: treat clicks in a portaled popover panel
 *  as "inside" so they don't close the surface under the popover. Pass `self`
 *  (the surface's trigger) so the panel HOSTING it counts as outside. */
export function isInsideAnchoredPopover(target: EventTarget | null, self?: Node | null): boolean {
  if (!(target instanceof Element)) return false;
  const panel = target.closest(`[${POPOVER_PANEL_ATTR}]`);
  if (!panel) return false;
  return !(self && panel.contains(self));
}

/**
 * Focus an element rendered inside an AnchoredPopover. The panel stays
 * visibility:hidden until Floating UI has positioned it, and a hidden field
 * refuses focus — so retry each frame until it takes. Returns a cancel fn.
 */
export function focusWhenVisible(el: HTMLElement | null, onFocused?: () => void): () => void {
  let frame = 0;
  const attempt = () => {
    if (!el) return;
    el.focus();
    if (document.activeElement !== el) frame = requestAnimationFrame(attempt);
    else onFocused?.();
  };
  attempt();
  return () => cancelAnimationFrame(frame);
}

/** Read a JSX element's ref for cloneElement composition. React 19 moved refs
 *  into props and warns on `element.ref`; React 18 keeps it off props. */
export function getElementRef(element: React.ReactElement): React.Ref<unknown> | undefined {
  const anyEl = element as any;
  if (parseInt(React.version, 10) >= 19) return anyEl.props?.ref ?? undefined;
  return anyEl.ref ?? undefined;
}

const POSITIONING_TOKEN =
  /^(?:(?:sm|md|lg|xl|2xl):)?(?:absolute|fixed|relative|sticky|static|-?(?:inset|top|bottom|left|right|z|m|mt|mb|ml|mr|mx|my)-.+)$/;

/** Drops position/margin/z-index utilities from a legacy anchor className so
 *  the remaining visual classes can style a Floating UI-positioned panel. */
export function stripPositioningClasses(className: string): string {
  return className
    .split(/\s+/)
    .filter((token) => token && !POSITIONING_TOKEN.test(token))
    .join(" ");
}

type NestingContextValue = {
  addToAncestors: (el: HTMLElement) => () => void;
  getAncestorPanels: () => HTMLElement[];
};

const NestingContext = React.createContext<NestingContextValue | null>(null);

// One shared background-scroll lock held while ANY locking popover/sheet is
// open (nested surfaces stack; releases when the last closes). It stops all
// user scrolling behind the popover: body overflow hidden kills page scroll
// and the scrollbar (position verified preserved), and the wheel/touch
// blockers cover inner scroll containers. Scrolling INSIDE a panel or a
// marked surface keeps working; programmatic scrolling (make-room) is
// unaffected. Trackpad momentum already in flight when the popover opens
// cannot be cancelled — a browser rule — and coasts out.
// Every locker shares scrollLockCount, so interleaved close orders can never
// strand the page. Wheel blocking is counted separately: body-only holders
// (useBodyScrollLock) scroll their own content and must not inherit it.
let scrollLockCount = 0;
let wheelBlockCount = 0;
let savedBodyOverflow = "";
let savedBodyPaddingRight = "";
// False when something outside this module had already hidden body overflow
// before the first lock engaged — restoring our snapshot on release would then
// resurrect that "hidden" after the foreign locker cleaned up, leaving the page
// permanently unscrollable.
let lockOwnsBodyStyles = false;

/** Marks a surface (mobile sheet, custom modal card) whose inside may keep
 *  scrolling while the shared lock is held. */
export const SCROLL_LOCK_ALLOW_ATTR = "data-scroll-lock-allow";

function blockScrollOutsidePanels(e: Event) {
  const target = e.target;
  if (isInsideAnchoredPopover(target)) return;
  if (
    target instanceof Element &&
    target.closest(`[data-radix-popper-content-wrapper], [${SCROLL_LOCK_ALLOW_ATTR}]`)
  ) {
    return;
  }
  e.preventDefault();
}

function acquireScrollLock(blockWheel = true): () => void {
  if (typeof document === "undefined") return () => {};
  if (scrollLockCount === 0) {
    lockOwnsBodyStyles = document.body.style.overflow !== "hidden";
    if (lockOwnsBodyStyles) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      savedBodyOverflow = document.body.style.overflow;
      savedBodyPaddingRight = document.body.style.paddingRight;
      document.body.style.overflow = "hidden";
      if (scrollbarWidth > 0) {
        const current = parseFloat(getComputedStyle(document.body).paddingRight) || 0;
        document.body.style.paddingRight = `${current + scrollbarWidth}px`;
      }
    }
  }
  scrollLockCount++;
  if (blockWheel) {
    if (wheelBlockCount === 0) {
      document.addEventListener("wheel", blockScrollOutsidePanels, { passive: false, capture: true });
      document.addEventListener("touchmove", blockScrollOutsidePanels, { passive: false, capture: true });
    }
    wheelBlockCount++;
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    if (blockWheel) {
      wheelBlockCount--;
      if (wheelBlockCount === 0) {
        document.removeEventListener("wheel", blockScrollOutsidePanels, { capture: true } as any);
        document.removeEventListener("touchmove", blockScrollOutsidePanels, { capture: true } as any);
      }
    }
    scrollLockCount--;
    if (scrollLockCount === 0 && lockOwnsBodyStyles) {
      document.body.style.overflow = savedBodyOverflow;
      document.body.style.paddingRight = savedBodyPaddingRight;
    }
  };
}

/** Hold the shared background-scroll lock while `active` — for surfaces that
 *  aren't AnchoredPopovers (mobile sheets, custom modals). Pair with
 *  SCROLL_LOCK_ALLOW_ATTR on the surface so its own content still scrolls. */
export function useAnchoredScrollLock(active: boolean) {
  React.useEffect(() => {
    if (!active) return;
    return acquireScrollLock();
  }, [active]);
}

/** Body-overflow half of the lock, for modals that own their own overlay and
 *  scroll their own content: joins the same refcount (so a popover closing over
 *  an open modal can't unlock the page, and the modal closing later can't
 *  re-assert a stale "hidden"), without blocking wheel events its content
 *  already handles. */
export function useBodyScrollLock(active: boolean) {
  React.useEffect(() => {
    if (!active) return;
    return acquireScrollLock(false);
  }, [active]);
}

/** Imperative `useBodyScrollLock` for non-React callers (ProseMirror plugins).
 *  Call the returned function exactly once, when the surface closes. */
export function acquireBodyScrollLock(): () => void {
  return acquireScrollLock(false);
}

/** True while any surface holds the shared lock — lets a failsafe unlock clear
 *  only genuinely leaked styles instead of a live lock. */
export function hasScrollLock(): boolean {
  return scrollLockCount > 0;
}

export interface AnchoredPopoverProps {
  /** Trigger element, or a viewport point for cursor-anchored context menus. */
  anchor: AnchoredPopoverAnchor;
  /** Enables built-in dismissal (outside pointerdown + Escape). */
  onClose?: () => void;
  placement?: Placement;
  /** Gap between anchor and panel, px. */
  offsetPx?: number;
  /** Min distance kept from the boundary edges, px. */
  padding?: number;
  /** Panel width follows the anchor width (select-style menus). */
  matchAnchorWidth?: boolean;
  /** Cap the panel height to the available space and scroll inside. Turn off
   *  for shells that still contain legacy absolutely-positioned submenus,
   *  which an `overflow: auto` panel would clip. */
  constrainHeight?: boolean;
  /** Explicit boundary override; defaults to the anchor's nearest
   *  [data-popover-boundary] ancestor, else the viewport. */
  boundary?: HTMLElement | null;
  /** Treated as inside for dismissal, so the control that opened the popover
   *  toggles it instead of closing and reopening. Only needed when the anchor
   *  is a point rather than that element. */
  excludeEl?: HTMLElement | null;
  /** Stop all background scrolling while the popover is open (default true);
   *  scrolling inside the panel keeps working. Pass false for hover
   *  surfaces. */
  lockScroll?: boolean;
  zIndex?: number;
  role?: string;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}

export function AnchoredPopover({
  anchor,
  onClose,
  placement = "bottom-start",
  offsetPx = 4,
  padding = 8,
  matchAnchorWidth = false,
  constrainHeight = true,
  boundary,
  excludeEl,
  lockScroll = true,
  zIndex = 9999,
  role = "dialog",
  className = "",
  style,
  children,
}: AnchoredPopoverProps) {
  const parentNesting = React.useContext(NestingContext);
  // Panels of descendant popovers (portaled to <body>, so not in our DOM
  // subtree) — the outside-pointerdown check must treat them as inside.
  const descendantPanels = React.useRef(new Set<HTMLElement>());
  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;
  const excludeElRef = React.useRef(excludeEl);
  excludeElRef.current = excludeEl;

  const anchorPoint =
    anchor && !(anchor instanceof HTMLElement) && !("current" in anchor) ? anchor : null;
  const anchorRefObj =
    anchor && !(anchor instanceof HTMLElement) && "current" in anchor ? anchor : null;
  // Ref-object anchors resolve at commit time (the ref is attached before any
  // layout effect runs), so a popover mounted already-open still finds it.
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(
    anchor instanceof HTMLElement ? anchor : null,
  );
  React.useLayoutEffect(() => {
    setAnchorEl(anchor instanceof HTMLElement ? anchor : (anchorRefObj?.current ?? null));
  });

  const resolvedBoundary = React.useMemo(() => {
    if (boundary !== undefined) return boundary ?? undefined;
    return (anchorEl?.closest(`[${POPOVER_BOUNDARY_ATTR}]`) as HTMLElement | null) ?? undefined;
  }, [boundary, anchorEl]);

  const middleware = React.useMemo(() => {
    const overflowOpts = { padding, ...(resolvedBoundary ? { boundary: resolvedBoundary } : {}) };
    const mainSide = placement.split("-")[0];
    const isVertical = mainSide === "top" || mainSide === "bottom";
    // Notion-style stability for top/bottom panels: keep the preferred side
    // and shrink+scroll there; only flip when the space is truly cramped
    // (< MIN_FLIP_SPACE) AND the other side is larger — so consecutive menus
    // stop teleporting between above and below their triggers.
    const MIN_FLIP_SPACE = 220;
    const preferShrink = {
      name: "preferShrink",
      fn(state: any) {
        if (state.middlewareData.preferShrink?.done) return {};
        const rect = state.elements.reference?.getBoundingClientRect?.();
        if (!rect) return {};
        const b = resolvedBoundary?.getBoundingClientRect() ?? {
          top: 0,
          bottom: window.innerHeight,
        };
        const gap = offsetPx + padding;
        const below = b.bottom - rect.bottom - gap;
        const above = rect.top - b.top - gap;
        const side = state.placement.split("-")[0];
        const preferred = side === "bottom" ? below : above;
        const opposite = side === "bottom" ? above : below;
        const need = Math.min(MIN_FLIP_SPACE, state.rects.floating.height);
        if (preferred >= need || preferred >= opposite) return { data: { done: true } };
        const alignment = state.placement.split("-")[1];
        const flippedSide = side === "bottom" ? "top" : "bottom";
        return {
          data: { done: true },
          reset: { placement: (alignment ? `${flippedSide}-${alignment}` : flippedSide) as Placement },
        };
      },
    };
    return [
      offset(offsetPx),
      // Vertical panels use preferShrink (stable side, shrink first). Side
      // flyouts (left/right) keep real flip so they swap sides when cramped.
      // crossAxis: false — shift's crossAxis handles that overflow below;
      // letting flip also react to it makes the two fight over placement.
      isVertical ? preferShrink : flip({ ...overflowOpts, crossAxis: false }),
      // crossAxis: true — when the panel fits on NEITHER side of the anchor,
      // slide it along the anchor axis until it's fully on-screen. Panels are
      // position:fixed, so unlike the old inline-absolute ones they can never
      // be scrolled into view; without this a tall panel hangs off the bottom.
      shift({ ...overflowOpts, crossAxis: true, limiter: limitShift() }),
      size({
        ...overflowOpts,
        apply({ availableWidth, availableHeight, rects, elements }) {
          const maxH = Math.max(0, availableHeight);
          const s: Partial<CSSStyleDeclaration> = {
            maxWidth: `${Math.max(0, availableWidth)}px`,
            // Hard ceiling even for constrainHeight={false} shells — a fixed
            // panel taller than the window is unreachable by any scrolling.
            maxHeight: `${maxH}px`,
          };
          if (matchAnchorWidth) s.width = `${rects.reference.width}px`;
          // Rigid content taller than the capped box must scroll inside —
          // with overflow visible it would spill past the viewport edge,
          // unreachable. While content fits, constrainHeight={false} keeps
          // overflow visible so legacy absolute submenus aren't clipped.
          const mustScroll = constrainHeight || elements.floating.scrollHeight > maxH + 1;
          s.overflowY = mustScroll ? "auto" : "";
          Object.assign(elements.floating.style, s);
        },
      }),
    ];
  }, [offsetPx, padding, resolvedBoundary, constrainHeight, matchAnchorWidth, placement]);

  const { refs, floatingStyles, isPositioned, update } = useFloating({
    placement,
    strategy: "fixed",
    // top/left instead of transform: the shared `modal-pop-in` entry animation
    // animates transform and would fight/reset a transform-based position.
    transform: false,
    middleware,
    whileElementsMounted: autoUpdate,
  });

  React.useLayoutEffect(() => {
    if (anchorEl) {
      refs.setReference(anchorEl);
    } else if (anchorPoint) {
      const { x, y } = anchorPoint;
      const virtual: VirtualElement = {
        getBoundingClientRect: () => ({
          x,
          y,
          top: y,
          bottom: y,
          left: x,
          right: x,
          width: 0,
          height: 0,
        }),
      };
      refs.setReference(virtual);
      update();
    }
  }, [anchorEl, anchorPoint?.x, anchorPoint?.y, refs, update]);

  // Register our panel with every ancestor popover, and let descendants
  // register with us (and, transitively, with our ancestors).
  const addToAncestors = React.useCallback(
    (el: HTMLElement) => {
      descendantPanels.current.add(el);
      const removeAbove = parentNesting?.addToAncestors(el);
      return () => {
        descendantPanels.current.delete(el);
        removeAbove?.();
      };
    },
    [parentNesting],
  );
  const getAncestorPanels = React.useCallback((): HTMLElement[] => {
    const above = parentNesting?.getAncestorPanels() ?? [];
    return panelRef.current ? [panelRef.current, ...above] : above;
  }, [parentNesting]);
  const nestingValue = React.useMemo(
    () => ({ addToAncestors, getAncestorPanels }),
    [addToAncestors, getAncestorPanels],
  );

  const setPanel = React.useCallback(
    (el: HTMLDivElement | null) => {
      panelRef.current = el;
      refs.setFloating(el);
    },
    [refs],
  );

  React.useEffect(() => {
    const el = panelRef.current;
    if (!el || !parentNesting) return;
    return parentNesting.addToAncestors(el);
  }, [parentNesting]);

  React.useEffect(() => {
    if (!lockScroll) return;
    return acquireScrollLock();
  }, [lockScroll]);

  // Notion-style "make room": when a downward popover opens with too little
  // space below its trigger, scroll the trigger's scroll container just
  // enough that the panel fits below — so menus consistently open downward
  // instead of alternating above/below with trigger height. Skipped for
  // fixed-position triggers (scrolling can't move them), hover surfaces
  // (lockScroll false), and when the container has no more room to give
  // (then the normal flip applies).
  // Runs BEFORE the first positioning (layout effect): the panel's content is
  // already in the DOM (hidden), so its height is measurable, and scrolling
  // now means the initial placement computation sees the roomy geometry —
  // no visible reposition afterwards.
  const madeRoomRef = React.useRef(false);
  React.useLayoutEffect(() => {
    if (madeRoomRef.current) return;
    if (!lockScroll || !placement.startsWith("bottom")) {
      madeRoomRef.current = true;
      return;
    }
    const panel = panelRef.current;
    // Anchor may resolve a pass later (sentinel/ref anchors) — don't latch
    // the once-guard until we could actually measure.
    if (!anchorEl || !panel) return;
    madeRoomRef.current = true;
    // Find the scroll container that actually moves the trigger. A fixed
    // ancestor (side/center peek panel) blocks page scrolling from moving it,
    // but a scrollable container INSIDE that fixed panel (the peek's own
    // scroller) still works — so only give up when the fixed boundary is hit
    // before any scrollable ancestor.
    let scroller: HTMLElement | null = null;
    for (let el: HTMLElement | null = anchorEl; el; el = el.parentElement) {
      const cs = getComputedStyle(el);
      if (el !== anchorEl && /(auto|scroll)/.test(cs.overflowY) && el.scrollHeight > el.clientHeight + 1) {
        scroller = el;
        break;
      }
      if (cs.position === "fixed") return;
    }
    const anchorRect = anchorEl.getBoundingClientRect();
    const boundaryRect = resolvedBoundary?.getBoundingClientRect() ?? {
      top: 0,
      bottom: window.innerHeight,
    };
    const gap = offsetPx + padding;
    const below = boundaryRect.bottom - anchorRect.bottom - gap;
    const maxUsable = boundaryRect.bottom - boundaryRect.top - 2 * padding;
    const needed = Math.min(panel.scrollHeight + 2, maxUsable);
    const deficit = Math.ceil(needed - below);
    if (deficit <= 0) return;
    const target = scroller ?? (document.scrollingElement as HTMLElement | null);
    if (!target) return;
    const room = target.scrollHeight - target.scrollTop - target.clientHeight;
    const delta = Math.min(deficit, Math.max(0, room));
    if (delta > 0) target.scrollBy({ top: delta });
  }, [lockScroll, placement, anchorEl, resolvedBoundary, offsetPx, padding]);

  // Focus restore: if focus moved into the panel while it was open, hand it
  // back to whatever had it before (usually the trigger) on close.
  React.useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    return () => {
      const active = document.activeElement;
      const focusLost =
        !active || active === document.body || (panelRef.current?.contains(active) ?? false);
      if (focusLost && prev?.isConnected && typeof prev.focus === "function") {
        prev.focus({ preventScroll: true });
      }
    };
  }, []);

  React.useEffect(() => {
    if (!onCloseRef.current) return;

    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target)) return;
      if (anchorEl?.contains(target)) return;
      if (excludeElRef.current?.contains(target)) return;
      for (const child of descendantPanels.current) {
        if (child.contains(target)) return;
      }
      // A click in another popover panel is inside (e.g. a legacy portaled
      // submenu opened from this one) — unless that panel hosts this popover.
      const ancestors = parentNesting?.getAncestorPanels() ?? [];
      if (isInsideAnchoredPopover(target) && !ancestors.some((a) => a.contains(target))) return;
      onCloseRef.current?.();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const target = e.target as Node | null;
      // Focus inside the panel (or a nested panel): the content owns Escape —
      // pickers clear their search first and close on the second press.
      if (target && panelRef.current?.contains(target)) return;
      for (const child of descendantPanels.current) {
        if (target && child.contains(target)) return;
      }
      // Innermost surface wins: without this, center peek / parent modals
      // listening on document would close on the same Escape.
      e.stopPropagation();
      e.stopImmediatePropagation();
      onCloseRef.current?.();
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [anchorEl, parentNesting]);

  if (typeof document === "undefined" || !anchor) return null;
  // A detached anchor measures 0×0 at the origin — the panel would render
  // orphaned at the top-left of the window.
  if (anchorEl && !anchorEl.isConnected) return null;

  const panel = (
    <NestingContext.Provider value={nestingValue}>
      <div
        ref={setPanel}
        role={role}
        {...{ [POPOVER_PANEL_ATTR]: "" }}
        className={`modal-pop-in font-sans ${className}`}
        style={{
          ...floatingStyles,
          zIndex,
          // Hide the pre-measure frame so the panel never flashes at 0,0.
          ...(isPositioned ? {} : { visibility: "hidden" as const }),
          ...style,
        }}
        // React portals bubble synthetic events through the COMPONENT tree, so
        // without this a click in the panel would still reach the trigger
        // cell's onClick toggle and close/reopen the popover.
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </NestingContext.Provider>
  );

  return createPortal(panel, document.body);
}

/**
 * Drop-in replacement for legacy `absolute top-full …` overlays rendered
 * inside a `relative` container: a zero-size sentinel resolves that container
 * as the anchor, so call sites don't need to thread a ref.
 */
export function SelfAnchoredPopover({
  children,
  ...rest
}: Omit<AnchoredPopoverProps, "anchor">) {
  const [parentEl, setParentEl] = React.useState<HTMLElement | null>(null);
  // Stable identity — an inline callback re-runs (null, el) every render and
  // would blank the anchor for a frame each time.
  const sentinelRef = React.useCallback((el: HTMLSpanElement | null) => {
    if (!el) return;
    let parent = el.parentElement as HTMLElement | null;
    // display:contents ancestors generate no box — their rect is 0×0 at the
    // origin, which would anchor the popover to the window's top-left corner.
    while (parent && getComputedStyle(parent).display === "contents") {
      parent = parent.parentElement as HTMLElement | null;
    }
    setParentEl((prev) => (prev === parent ? prev : parent));
  }, []);
  return (
    <>
      <span aria-hidden className="hidden" ref={sentinelRef} />
      <AnchoredPopover anchor={parentEl} {...rest}>
        {children}
      </AnchoredPopover>
    </>
  );
}
